import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { Step } from "../types/sop";
import {
  sopService,
  type RegenerateSopStepsResponse,
  type RegenerateFileType,
  type RegenerateSopLocksResponse,
} from "../api/sopService";
import { uploadFileToS3, type UploadResponse } from "../utils/uploadFileToS3";

export type RegenerationDecision = "accept" | "request_change";
export type RegeneratedCandidate = RegenerateSopStepsResponse["candidates"][number];

export type ReferenceFileKind = "bom" | "assembly" | "video" | "step";

export function getReferenceFileKey(file: File): string {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

function isUploadResponse(
  value: UploadResponse | string | undefined,
): value is UploadResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    "media_id" in value &&
    "upload_url" in value
  );
}

function toRegenerateFileType(kind: ReferenceFileKind): RegenerateFileType {
  switch (kind) {
    case "bom":
      return "bom";
    case "assembly":
      return "assembly";
    case "video":
      return "video";
    case "step":
      return "step_file";
  }
}

interface RegeneratedStepsState {
  referenceFiles: File[];
  referenceFileKinds: Record<string, ReferenceFileKind>;
  defaultReferenceFileKind: ReferenceFileKind;
  selectedStepIndices: number[];
  selectedFeedbackIds: string[];
  candidates: RegeneratedCandidate[];
  decisions: Record<number, RegenerationDecision>;
  candidatesDirty: boolean;
  isRegenerating: boolean;
  error: string | null;
  reviewMode: boolean;
  /** Media IDs uploaded during the most recent regeneration session. Used to clean up denied files. */
  uploadedMediaIds: string[];
  regenerationLocks: RegenerateSopLocksResponse | null;

  setReferenceFiles: (files: File[]) => void;
  setDefaultReferenceFileKind: (kind: ReferenceFileKind) => void;
  setReferenceFileKind: (fileKey: string, kind: ReferenceFileKind) => void;
  removeReferenceFileAt: (index: number) => void;
  clearReferenceFiles: () => void;

  setSelectedStepIndices: (indices: number[]) => void;
  toggleStepIndex: (index: number) => void;
  clearSelectedSteps: () => void;
  setSelectedFeedbackIds: (ids: string[]) => void;
  toggleFeedback: (feedbackId: string) => void;
  clearFeedbacks: () => void;

  setCandidates: (candidates: RegeneratedCandidate[]) => void;
  clearCandidates: () => void;

  decide: (stepIndex: number, decision: RegenerationDecision) => void;
  undecide: (stepIndex: number) => void;
  clearDecisions: () => void;

  setReviewMode: (value: boolean) => void;
  updateCandidate: (stepIndex: number, updatedStep: Step) => void;
  saveCandidates: (args: {
    sopId: string;
    versionId: string;
    notifyOwner?: boolean;
  }) => Promise<{ success: boolean; error?: string }>;
  applyCandidateDecisions: (args: {
    sopId: string;
    versionId: string;
    currentSteps: Step[];
  }) => Promise<{ success: boolean; error?: string; versionConflict?: boolean; changeRequestCount?: number }>;
  discardWithCleanup: (args: {
    sopId: string;
    versionId: string;
  }) => Promise<{ success: boolean; error?: string }>;

  resetAll: () => void;
  regenerate: (args: {
    sopId: string;
    versionId: string;
  }) => Promise<{ success: boolean; error?: string }>;

  buildFinalSteps: (currentSteps: Step[]) => Step[];
  reviewedCount: number;
  totalCandidates: number;
  allDecided: boolean;
}

export const useRegeneratedStepsStore = create<RegeneratedStepsState>()(
  devtools(
    (set, get) => ({
      referenceFiles: [],
      referenceFileKinds: {},
      defaultReferenceFileKind: "step",
      selectedStepIndices: [],
      selectedFeedbackIds: [],
      candidates: [],
      decisions: {},
      candidatesDirty: false,
      isRegenerating: false,
      error: null,
      reviewMode: false,
      uploadedMediaIds: [],
      regenerationLocks: null,

      setReferenceFiles: (files) =>
        set((state) => {
          const prevKinds = state.referenceFileKinds;
          const nextKinds: Record<string, ReferenceFileKind> = {};
          files.forEach((f) => {
            const key = getReferenceFileKey(f);
            nextKinds[key] = prevKinds[key] ?? state.defaultReferenceFileKind;
          });
          return { referenceFiles: files, referenceFileKinds: nextKinds };
        }),

      setDefaultReferenceFileKind: (kind) => set({ defaultReferenceFileKind: kind }),

      setReferenceFileKind: (fileKey, kind) =>
        set((state) => ({
          referenceFileKinds: { ...state.referenceFileKinds, [fileKey]: kind },
        })),

      removeReferenceFileAt: (index) =>
        set((state) => {
          const nextFiles = state.referenceFiles.filter((_, i) => i !== index);
          const nextKinds: Record<string, ReferenceFileKind> = {};
          nextFiles.forEach((f) => {
            const key = getReferenceFileKey(f);
            nextKinds[key] = state.referenceFileKinds[key] ?? state.defaultReferenceFileKind;
          });
          return { referenceFiles: nextFiles, referenceFileKinds: nextKinds };
        }),

      clearReferenceFiles: () => set({ referenceFiles: [], referenceFileKinds: {} }),

      setSelectedStepIndices: (indices) => set({ selectedStepIndices: indices }),
      toggleStepIndex: (index) =>
        set((state) => ({
          selectedStepIndices: state.selectedStepIndices.includes(index)
            ? state.selectedStepIndices.filter((i) => i !== index)
            : [...state.selectedStepIndices, index],
        })),
      clearSelectedSteps: () => set({ selectedStepIndices: [] }),
      setSelectedFeedbackIds: (ids) => set({ selectedFeedbackIds: ids }),
      toggleFeedback: (feedbackId) =>
        set((state) => ({
          selectedFeedbackIds: state.selectedFeedbackIds.includes(feedbackId)
            ? state.selectedFeedbackIds.filter((id) => id !== feedbackId)
            : [...state.selectedFeedbackIds, feedbackId],
        })),
      clearFeedbacks: () => set({ selectedFeedbackIds: [] }),

      setCandidates: (candidates) => set({ candidates, candidatesDirty: false }),
      clearCandidates: () => set({ candidates: [], candidatesDirty: false }),

      decide: (stepIndex, decision) =>
        set((state) => ({
          decisions: { ...state.decisions, [stepIndex]: decision },
        })),
      undecide: (stepIndex) =>
        set((state) => {
          const next = { ...state.decisions };
          delete next[stepIndex];
          return { decisions: next };
        }),
      clearDecisions: () => set({ decisions: {} }),

      setReviewMode: (value) => set({ reviewMode: value }),

      updateCandidate: (stepIndex, updatedStep) =>
        set((state) => ({
          candidates: state.candidates.map((c) =>
            c.stepIndex === stepIndex ? { ...c, candidate: updatedStep } : c,
          ),
          candidatesDirty: true,
        })),

      saveCandidates: async ({ sopId, versionId, notifyOwner }) => {
        const { candidates } = get();
        try {
          await sopService.updateCandidates(
            sopId,
            versionId,
            candidates.map((c) => ({ stepIndex: c.stepIndex, candidate: c.candidate })),
            notifyOwner,
          );
          set({ candidatesDirty: false });
          return { success: true };
        } catch (e) {
          const message =
            e instanceof Error ? e.message : "Failed to save candidates.";
          return { success: false, error: message };
        }
      },

      applyCandidateDecisions: async ({ sopId, versionId, currentSteps }) => {
        const { buildFinalSteps, candidates, decisions, uploadedMediaIds } = get();
        const finalSteps = buildFinalSteps(currentSteps);

        // Collect change-requested candidate IDs — backend keeps them with needs_regen=true
        // so the admin can see and regenerate them using the attached feedback.
        const changeRequestCandidateIds = candidates
          .filter((c) => decisions[c.stepIndex] === "request_change")
          .map((c) => c.id)
          .filter(Boolean);
        const changeRequestCount = changeRequestCandidateIds.length;

        const allRequestedChange =
          candidates.length > 0 &&
          candidates.every((c) => decisions[c.stepIndex] === "request_change");
        const mediaIdsToDelete = allRequestedChange ? uploadedMediaIds : [];
        try {
          await sopService.applyCandidates(
            sopId, versionId, finalSteps, mediaIdsToDelete, changeRequestCandidateIds,
          );
          // Always clear local state. Change-requested steps show as locked because
          // processing_status='regenerate' keeps them in the admin queue.
          set({ candidates: [], decisions: {}, candidatesDirty: false, uploadedMediaIds: [] });
          return { success: true, changeRequestCount };
        } catch (e) {
          const err = e as { response?: { status?: number } };
          const versionConflict = err?.response?.status === 409;
          const message =
            e instanceof Error ? e.message : "Failed to apply changes.";
          return {
            success: false,
            error: message,
            versionConflict,
          };
        }
      },

      discardWithCleanup: async ({ sopId, versionId }) => {
        const { uploadedMediaIds } = get();
        try {
          await sopService.discardCandidates(sopId, versionId, uploadedMediaIds);
          set({ candidates: [], decisions: {}, candidatesDirty: false, uploadedMediaIds: [] });
          return { success: true };
        } catch (e) {
          const message =
            e instanceof Error ? e.message : "Failed to discard candidates.";
          return { success: false, error: message };
        }
      },

      resetAll: () =>
        set({
          referenceFiles: [],
          referenceFileKinds: {},
          defaultReferenceFileKind: "step",
          selectedStepIndices: [],
          selectedFeedbackIds: [],
          candidates: [],
          decisions: {},
          candidatesDirty: false,
          isRegenerating: false,
          error: null,
          reviewMode: false,
          uploadedMediaIds: [],
          regenerationLocks: null,
        }),

      regenerate: async ({ sopId, versionId }) => {
        const {
          referenceFiles,
          referenceFileKinds,
          selectedStepIndices,
          selectedFeedbackIds,
        } = get();
        if (
          !referenceFiles.length &&
          !selectedFeedbackIds.length
        ) {
          set({
            error: "Select at least one source: files or open feedback.",
          });
          return { success: false };
        }

        set({ isRegenerating: true, error: null });
        try {
          const mediaRefs: { mediaId: string; mediaType: RegenerateFileType }[] = [];

          for (const file of referenceFiles) {
            const kind = referenceFileKinds[getReferenceFileKey(file)] ?? "step";
            const mediaType = toRegenerateFileType(kind);
            const result = await uploadFileToS3(file, sopId);
            if (!isUploadResponse(result)) continue;
            mediaRefs.push({ mediaId: result.media_id, mediaType });
          }

          await sopService.regenerateSteps(
            sopId,
            versionId,
            {
              stepIndices: selectedStepIndices,
              feedbackIds: selectedFeedbackIds,
              mediaRefs,
            },
          );
          const [candidateResponse, regenerationLocks] = await Promise.all([
            sopService.getRegenerationCandidates(sopId, versionId),
            sopService.getRegenerationLocks(sopId, versionId),
          ]);
          set({
            candidates: candidateResponse.candidates,
            decisions: {},
            candidatesDirty: false,
            uploadedMediaIds: mediaRefs.map((r) => r.mediaId),
            regenerationLocks,
            reviewMode: true,
          });
          return { success: true };
        } catch (e) {
          const err = e as {
            response?: { data?: { error?: string } };
          };
          const message =
            err?.response?.data?.error ??
            (e instanceof Error ? e.message : "Failed to regenerate steps.");
          set({ error: message });
          return { success: false, error: message };
        } finally {
          set({ isRegenerating: false });
        }
      },

      buildFinalSteps: (currentSteps) => {
        const { candidates, decisions } = get();
        return currentSteps.map((step, index) => {
          const candidate = candidates.find((c) => c.stepIndex === index);
          if (!candidate) return step;
          return decisions[candidate.stepIndex] === "accept"
            ? candidate.candidate
            : step; // "request_change" keeps the original step
        });
      },

      get reviewedCount() {
        const { candidates, decisions } = get();
        return candidates.reduce(
          (count, c) => (decisions[c.stepIndex] ? count + 1 : count),
          0,
        );
      },
      get totalCandidates() {
        return get().candidates.length;
      },
      get allDecided() {
        const { candidates, decisions } = get();
        return (
          candidates.length > 0 &&
          candidates.every((c) => decisions[c.stepIndex] !== undefined)
        );
      },
    }),
    { name: "regenerate-steps-store" },
  ),
);

